import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { showToast as toast } from '../toastManager.js';

const QueueContext = createContext(null);

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_TICKS = Math.ceil((10 * 60 * 1000) / POLL_INTERVAL_MS); // 10 minutes

export function QueueProvider({ children }) {
    const [jobs, setJobs] = useState([]);
    const [collapsed, setCollapsed] = useState(false);
    const intervalsRef = useRef({});

    const updateJob = (jobId, patch) => {
        setJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, ...patch } : j));
    };

    const startPolling = (jobId, landingPage, app_url) => {
        let ticks = 0;

        const id = setInterval(async () => {
            ticks++;
            try {
                const resp = await axios.get(
                    app_url + `/api/firebase/job_status/${jobId}`,
                    { withCredentials: true }
                );
                const job = resp?.data?.job || {};
                updateJob(jobId, { status: job.status || '', progress: job.progress || [] });

                if (job.status === 'succeeded') {
                    clearInterval(id);
                    delete intervalsRef.current[jobId];
                    toast.success(`Section saved to "${landingPage}" successfully`);
                    setTimeout(() => setJobs(prev => prev.filter(j => j.jobId !== jobId)), 10000);
                } else if (job.status === 'failed') {
                    clearInterval(id);
                    delete intervalsRef.current[jobId];
                    toast.error(job?.error?.message || 'Failed to save section');
                    setTimeout(() => setJobs(prev => prev.filter(j => j.jobId !== jobId)), 10000);
                } else if (ticks >= MAX_POLL_TICKS) {
                    clearInterval(id);
                    delete intervalsRef.current[jobId];
                    updateJob(jobId, {
                        status: 'failed',
                        progress: [{ ts: new Date().toISOString(), message: 'Timed out. Please try again.' }]
                    });
                    toast.error('Save timed out. Please try again.');
                    setTimeout(() => setJobs(prev => prev.filter(j => j.jobId !== jobId)), 10000);
                }
            } catch (err) {
                clearInterval(id);
                delete intervalsRef.current[jobId];
                updateJob(jobId, { status: 'failed' });
                toast.error('Failed to get save status');
            }
        }, POLL_INTERVAL_MS);

        intervalsRef.current[jobId] = id;
    };

    const addJob = (jobId, sectionId, sectionName, landingPage, app_url) => {
        setJobs(prev => [{
            jobId,
            sectionId,
            sectionName: sectionName || sectionId,
            landingPage,
            jobType: 'save',
            status: 'queued',
            progress: [{ ts: new Date().toISOString(), message: 'Queued' }],
            conflicts: [],
            startedAt: new Date().toISOString(),
        }, ...prev]);
        setCollapsed(false);
        startPolling(jobId, landingPage, app_url);
    };

    const addInstantJob = (sectionId, sectionName, jobType, status, messages, conflicts) => {
        const jobId = `${jobType}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        const progress = (messages || []).map(msg => ({ ts: new Date().toISOString(), message: msg }));
        setJobs(prev => [{
            jobId,
            sectionId,
            sectionName: sectionName || sectionId,
            landingPage: '',
            jobType,
            status,
            progress: progress.length ? progress : [{ ts: new Date().toISOString(), message: status === 'succeeded' ? 'Done' : 'Failed' }],
            conflicts: conflicts || [],
            startedAt: new Date().toISOString(),
        }, ...prev]);
        setCollapsed(false);
        if (status === 'succeeded') {
            setTimeout(() => setJobs(prev => prev.filter(j => j.jobId !== jobId)), 10000);
        }
    };

    const removeJob = (jobId) => {
        if (intervalsRef.current[jobId]) {
            clearInterval(intervalsRef.current[jobId]);
            delete intervalsRef.current[jobId];
        }
        setJobs(prev => prev.filter(j => j.jobId !== jobId));
    };

    const clearCompleted = () => {
        setJobs(prev => prev.filter(j => j.status !== 'succeeded' && j.status !== 'failed'));
    };

    const toggleCollapsed = () => setCollapsed(prev => !prev);

    useEffect(() => {
        return () => {
            Object.values(intervalsRef.current).forEach(clearInterval);
        };
    }, []);

    return (
        <QueueContext.Provider value={{ jobs, addJob, addInstantJob, removeJob, clearCompleted, collapsed, toggleCollapsed }}>
            {children}
        </QueueContext.Provider>
    );
}

export function useQueue() {
    return useContext(QueueContext);
}